import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Building2,
  CalendarDays,
  ClipboardList,
  Handshake,
  Inbox,
  KanbanSquare,
  Loader2,
  LogOut,
  Trophy,
  Wallet,
} from "lucide-react";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { LeadsView } from "./admin/LeadsView";
import { PipelineView } from "./admin/PipelineView";
import { AgendaView } from "./admin/AgendaView";
import { FinanceiroView } from "./admin/FinanceiroView";
import { PrestadoresView } from "./admin/PrestadoresView";
import { ImobiliariasView } from "./admin/ImobiliariasView";
import { RankingView } from "./admin/RankingView";
import { LeadDetail } from "./admin/LeadDetail";

const NAV_ITEMS = [
  { key: "leads", label: "Leads", icon: Inbox },
  { key: "solicitacoes", label: "Solicitações", icon: ClipboardList },
  { key: "pipeline", label: "Pipeline", icon: KanbanSquare },
  { key: "agenda", label: "Agenda", icon: CalendarDays },
  { key: "financeiro", label: "Financeiro", icon: Wallet },
  { key: "prestadores", label: "Prestadores", icon: Handshake },
  { key: "imobiliarias", label: "Imobiliárias", icon: Building2 },
  { key: "ranking", label: "Ranking", icon: Trophy },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [leads, setLeads] = useState([]);
  const [loadingLeads, setLoadingLeads] = useState(false);
  const [view, setView] = useState("leads");
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        try {
          await api.post("/auth/refresh");
          const { data } = await api.get("/auth/me");
          setUser(data);
        } catch {
          navigate("/admin/login");
        }
      } finally {
        setChecking(false);
      }
    };
    check();
  }, [navigate]);

  const load = useCallback(async () => {
    setLoadingLeads(true);
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch {
      toast.error("Não foi possível carregar os dados.");
    } finally {
      setLoadingLeads(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100">
        <Loader2 className="h-8 w-8 animate-spin text-brand" data-testid="admin-loading" />
      </div>
    );
  }

  const isQualifiedService = (l) =>
    l.type === "service" &&
    (Boolean(l.payload?.service_type) ||
      Boolean(l.payload?.indicated_partner) ||
      l.status !== "aguardando_indicacao");

  const inboxLeads = leads.filter((l) =>
    l.type === "service" ? !isQualifiedService(l) : l.status !== "parceria_fechada"
  );
  const solicitacaoLeads = leads.filter(isQualifiedService);

  const selected = leads.find((l) => l.id === selectedId) || null;
  const openLead = (lead) => setSelectedId(lead.id);

  const navButton = (item, mobile = false) => (
    <button
      key={item.key}
      onClick={() => setView(item.key)}
      data-testid={`admin-nav-${item.key}${mobile ? "-mobile" : ""}`}
      className={
        mobile
          ? `inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-colors ${
              view === item.key ? "bg-petrol text-white" : "border border-slate-200 bg-white text-slate-600"
            }`
          : `flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-semibold transition-colors duration-200 ${
              view === item.key
                ? "bg-brand text-white"
                : "text-white/60 hover:bg-white/10 hover:text-white"
            }`
      }
    >
      <item.icon className="h-4.5 w-4.5" />
      {item.label}
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-100 lg:flex" data-testid="admin-dashboard">
      <aside
        className="hidden lg:sticky lg:top-0 lg:flex lg:h-screen lg:w-60 lg:shrink-0 lg:flex-col lg:bg-petrol"
        data-testid="admin-sidebar"
      >
        <div className="border-b border-white/10 px-5 py-6">
          <Logo dark size={30} dataTestId="admin-logo" />
        </div>
        <nav className="flex-1 space-y-1 p-4" aria-label="Menu do painel">
          {NAV_ITEMS.map((item) => navButton(item))}
        </nav>
        <div className="border-t border-white/10 p-4">
          <p className="truncate px-2 font-mono text-[0.65rem] text-white/40" data-testid="admin-user-email">
            {user?.email}
          </p>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="mt-2 flex w-full items-center gap-2 rounded-xl px-3 py-2 text-sm font-medium text-white/60 transition-colors hover:bg-white/10 hover:text-white"
          >
            <LogOut className="h-4 w-4" />
            Sair
          </button>
        </div>
      </aside>

      <div className="min-w-0 flex-1">
        <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-slate-200 bg-white/90 px-5 backdrop-blur-xl lg:hidden">
          <Logo size={28} dataTestId="admin-logo-mobile" />
          <button
            onClick={logout}
            data-testid="admin-logout-button-mobile"
            aria-label="Sair"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 text-slate-500"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </header>
        <nav className="flex gap-2 overflow-x-auto px-5 pt-4 lg:hidden" aria-label="Menu do painel">
          {NAV_ITEMS.map((item) => navButton(item, true))}
        </nav>

        <main className="mx-auto max-w-7xl px-5 py-8 sm:px-8">
          {view === "leads" && (
            <LeadsView leads={inboxLeads} loading={loadingLeads} onRefresh={load} onOpen={openLead} />
          )}
          {view === "solicitacoes" && (
            <LeadsView
              leads={solicitacaoLeads}
              loading={loadingLeads}
              onRefresh={load}
              onOpen={openLead}
              mode="solicitacoes"
            />
          )}
          {view === "financeiro" && <FinanceiroView leads={leads} onChanged={load} />}
          {view === "pipeline" && (
            <PipelineView leads={leads} onOpen={openLead} onChanged={load} />
          )}
          {view === "agenda" && <AgendaView leads={leads} onOpen={openLead} />}
          {view === "prestadores" && (
            <PrestadoresView leads={leads} onOpen={openLead} onChanged={load} />
          )}
          {view === "imobiliarias" && (
            <ImobiliariasView leads={leads} onOpen={openLead} onChanged={load} />
          )}
          {view === "ranking" && <RankingView leads={leads} onOpen={openLead} />}
        </main>
      </div>

      <LeadDetail
        lead={selected}
        context={view}
        onClose={() => setSelectedId(null)}
        onChanged={load}
      />
    </div>
  );
}
